import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SnackbarUtils from 'utils/SnackbarUtils';
import { Col } from '../../../components/Col';
import { CustomButton } from '../../../components/CustomButton';
import { CustomTextField } from '../../../components/CustomTextField';
import { GenericModal } from '../../../components/GenericModal';
import RefreshIcon from '../../../components/Icons/RefreshIcon';
import SaveIcon from '../../../components/Icons/SaveIcon';
import { Row } from '../../../components/Row';
import {
  changeIdebEditingValue,
  clearIdebEditing,
  createIdeb,
  loadIdebById,
  saveIdeb,
} from '../../../store/modules/EscolasActions';
import { IDEB_AVERAGE_DECIMALS_TOTAL } from '../../../store/modules/EscolasReducer';
import { closeModal } from '../../../store/modules/ModalActions';
import { isAuthorizedRequest } from '../../../utils/httpResponseHandlers';
import { getInputFocusInFocusOutAttr } from '../../../utils/HTMLUtils';
import {
  allIsTrue,
  averageIsValid,
  requiredIsValid,
  yearIsValid,
} from '../../../utils/ValidationUtils';
import styles from './IdebEditModal.scss';

const rootClassName = styles.rootClassName;
const rootElementId = 'ideb-edit';

const INITIAL_STATE = {
  authorizedRequest: true,
  disableBackdropClick: false,
  disableEscapeKeyDown: false,
  sending: false,
  loading: false,
  anoChanged: false,
  mediaChanged: false,
  metaChanged: false,
};

const IdebEditModal = ({
  idEscola,
  id,
  opened,
  modalKey,
  cancelCallback,
  savedCallback,
}) => {
  const [state, setState] = useState(INITIAL_STATE);
  const [, setFocusedFieldName] = useState(null);

  const dispatch = useDispatch();
  const languageValues = useSelector((languageState) => languageState.languages.languageValues);
  const escolas = useSelector((escolasState) => escolasState.escolas);
  const auth = useSelector((authState) => authState.auth);

  useEffect(() => {
    let newState = {};
    if (editing()) {
      if (auth.userPermissions.escolas.ideb.edit) {
        loadIdeb();
      } else {
        newState.authorizedRequest = false;
      }
    }
    setState((prevState) => ({
      ...prevState,
      ...newState,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, auth]);

  useEffect(() => {
    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadIdeb = () => {
    setState((prevState) => ({
      ...prevState,
      loading: true,
      disableBackdropClick: true,
      disableEscapeKeyDown: true,
    }));
    dispatch(
      loadIdebById(
        id,
        () => {
          setState((prevState) => ({
            ...prevState,
            loading: false,
            anoChanged: true,
            mediaChanged: true,
            metaChanged: true,
            disableBackdropClick: false,
            disableEscapeKeyDown: false,
          }));
        },
        (error) => {
          SnackbarUtils.enqueueSnackbar({
            message:
              error.response.statusText || languageValues.errorLoadIdebEditing,
            options: {
              variant: 'error',
            },
          });
          setState((prevState) => ({
            ...prevState,
            disableEscapeKeyDown: false,
            disableBackdropClick: false,
          }));
          toClose();
        }
      )
    );
  };

  const editing = () => (id ? true : false);

  const toClose = () => {
    if (typeof cancelCallback === 'function') {
      cancelCallback();
    }
    dispatch(closeModal(modalKey));
  };

  const getTitle = () => {
    if (editing()) {
      return languageValues.editIdebTitle;
    }
    return languageValues.newIdebTitle;
  };

  const setValue = (event) => {
    setState((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
      [event.target.name + 'Changed']: true,
    }));
    dispatch(changeIdebEditingValue(event.target.name, event.target.value));
  };

  const anoValueIsValid = () => {
    const value = escolas.idebEditing.ano;
    const required = requiredIsValid(
      value,
      languageValues.validations.ano.required
    );
    const length = yearIsValid(value, languageValues.validations.ano.length);
    if (!required.status) {
      return required;
    }
    if (!length.status) {
      return length;
    }
    return {
      status: true,
    };
  };

  const mediaValueIsValid = () => {
    const value = escolas.idebEditing.media;
    const required = requiredIsValid(
      value,
      languageValues.validations.media.required
    );
    const length = averageIsValid(
      value,
      languageValues.validations.media.length
    );
    if (!required.status) {
      return required;
    }
    if (!length.status) {
      return length;
    }
    return {
      status: true,
    };
  };

  const metaValueIsValid = () => {
    const value = escolas.idebEditing.meta;
    const required = requiredIsValid(
      value,
      languageValues.validations.meta.required
    );
    const length = averageIsValid(
      value,
      languageValues.validations.meta.length
    );
    if (!required.status) {
      return required;
    }
    if (!length.status) {
      return length;
    }
    return {
      status: true,
    };
  };

  const getSubmitLabel = () => {
    if (editing()) {
      return languageValues.salvar;
    }
    return languageValues.cadastrar;
  };

  const processing = () => (state.sending || state.loading ? true : false);

  const formIsValid = () => {
    const validations = [
      editing() ? true : anoValueIsValid().status,
      mediaValueIsValid().status,
      metaValueIsValid().status,
    ];
    if (processing() || !allIsTrue(validations)) {
      return false;
    }
    return true;
  };

  const reset = () => {
    setState(INITIAL_STATE);
    dispatch(clearIdebEditing());
  };

  const toSubmit = () => {
    if (!formIsValid()) {
      return;
    }
    setState((prevState) => ({
      ...prevState,
      sending: true,
      disableBackdropClick: true,
      disableEscapeKeyDown: true,
    }));
    let ideb = {
      id: escolas.idebEditing.id,
      ano: escolas.idebEditing.ano,
      idEscola: idEscola,
      media: parseFloat(escolas.idebEditing.media),
      meta: parseFloat(escolas.idebEditing.meta),
    };

    const saveFunction = editing() ? saveIdeb : createIdeb;
    dispatch(
      saveFunction(
        ideb,
        () => {
          SnackbarUtils.enqueueSnackbar({
            message: editing()
              ? languageValues.successSalvarIdeb
              : languageValues.successCadastrarIdeb,
            options: {
              variant: 'success',
            },
          });
          setState((prevState) => ({
            ...prevState,
            sending: false,
            disableBackdropClick: false,
            disableEscapeKeyDown: false,
          }));
          if (typeof savedCallback === 'function') {
            savedCallback();
          }
          toClose();
        },
        (error) => {
          if (isAuthorizedRequest(error.response)) {
            SnackbarUtils.enqueueSnackbar({
              message:
                error.response.statusText ||
                (editing()
                  ? languageValues.erroSalvarIdeb
                  : languageValues.erroCadastrarIdeb),
              options: {
                variant: 'error',
              },
            });
            setState((prevState) => ({
              ...prevState,
              sending: false,
              disableBackdropClick: false,
              disableEscapeKeyDown: false,
            }));
          } else {
            setState((prevState) => ({
              ...prevState,
              sending: false,
              authorizedRequest: false,
              disableBackdropClick: false,
              disableEscapeKeyDown: false,
            }));
          }
        }
      )
    );
  };

  const getAverageLimit = (average, decimalLimit = 1) => {
    const averageStr = String(average);
    const pointIndex = averageStr.indexOf('.');
    if (pointIndex > -1) {
      return pointIndex + (decimalLimit + 1);
    }
    return 4;
  };

  return (
    <GenericModal
      id={rootElementId}
      onClose={() => {
        toClose();
      }}
      titleHint={languageValues.IdebEditModal}
      DialogProps={{
        open: opened,
        maxWidth: 'sm',
        disableBackdropClick: state.disableBackdropClick,
        disableEscapeKeyDown: state.disableEscapeKeyDown,
      }}
      title={
        <>
          {getTitle()}
          {state.loading || state.sending ? (
            <RefreshIcon className={rootClassName + '-loading-icon'} />
          ) : null}
        </>
      }
      sending={false}
      footerContent={
        <Row>
          <Col cols={[12, 12, 6, 6, 6]}>
            <CustomButton
              theme='pufvYellow'
              className={rootClassName + '-cancel-button'}
              label={languageValues.cancelButton}
              onClick={() => toClose()}
            />
          </Col>
          <Col cols={[12, 12, 6, 6, 6]}>
            <CustomButton
              theme='pufvYellow'
              className={rootClassName + '-save-buttom'}
              label={getSubmitLabel()}
              disabled={!formIsValid()}
              rightIcon={<SaveIcon />}
              type='submit'
            />
          </Col>
        </Row>
      }
      onSubmit={(event) => {
        event.preventDefault();
        toSubmit();
      }}
    >
      <Row>
        <Col cols={[12, 12, 12, 12, 12]}>
          <CustomTextField
            label={languageValues.cadastroIdebAno}
            name='ano'
            type='tel'
            maxSize={4}
            value={escolas.idebEditing.ano}
            onChange={(event) => {
              setValue(event);
            }}
            disabled={editing()}
            helperText={
              state.anoChanged && !anoValueIsValid().status
                ? anoValueIsValid().error
                : ''
            }
            error={state.anoChanged && !anoValueIsValid().status}
            {...getInputFocusInFocusOutAttr(setFocusedFieldName, () =>
              setFocusedFieldName(null)
            )}
          />
        </Col>
        <Col cols={[12, 12, 12, 12, 12]}>
          <CustomTextField
            label={languageValues.cadastroIdebMedia}
            name='media'
            type='tel'
            maxSize={getAverageLimit(
              escolas.idebEditing.media,
              IDEB_AVERAGE_DECIMALS_TOTAL
            )}
            value={escolas.idebEditing.media}
            onChange={(event) => {
              setValue(event);
            }}
            limitAlertMessage={languageValues.decimalNumberFieldIsLonger
              // eslint-disable-next-line no-template-curly-in-string
              .split('${1}')
              .join(IDEB_AVERAGE_DECIMALS_TOTAL)}
            helperText={
              state.mediaChanged && !mediaValueIsValid().status
                ? mediaValueIsValid().error
                : ''
            }
            error={state.mediaChanged && !mediaValueIsValid().status}
            {...getInputFocusInFocusOutAttr(setFocusedFieldName, () =>
              setFocusedFieldName(null)
            )}
          />
        </Col>
        <Col cols={[12, 12, 12, 12, 12]}>
          <CustomTextField
            label={languageValues.cadastroIdebMeta}
            name='meta'
            type='tel'
            maxSize={getAverageLimit(
              escolas.idebEditing.meta,
              IDEB_AVERAGE_DECIMALS_TOTAL
            )}
            value={escolas.idebEditing.meta}
            onChange={(event) => {
              setValue(event);
            }}
            limitAlertMessage={languageValues.decimalNumberFieldIsLonger
              // eslint-disable-next-line no-template-curly-in-string
              .split('${1}')
              .join(IDEB_AVERAGE_DECIMALS_TOTAL)}
            helperText={
              state.metaChanged && !metaValueIsValid().status
                ? metaValueIsValid().error
                : ''
            }
            error={state.metaChanged && !metaValueIsValid().status}
            {...getInputFocusInFocusOutAttr(setFocusedFieldName, () =>
              setFocusedFieldName(null)
            )}
          />
        </Col>
      </Row>
    </GenericModal>
  );
};

IdebEditModal.propTypes = {
  idEscola: PropTypes.string,
  id: PropTypes.number,
  opened: PropTypes.bool,
  modalKey: PropTypes.string.isRequired,
  cancelCallback: PropTypes.func,
  savedCallback: PropTypes.func,
};

export default IdebEditModal;
